/**
 * Module temperature decoding for the Meskernel merchandiser sensors.
 *
 * Shared by the two places a module temperature enters the application: the
 * cellular readings ingest (crm-api device payload transformer) and the
 * Bluetooth ranging read on the Provision Sensors screen. One implementation
 * keeps the two from disagreeing about the same unit.
 *
 * Raw `t` is in 0.1 °C units, sent by the firmware as an UNSIGNED 32-bit
 * integer: a below-freezing reading arrives two's-complement wrapped
 * (-2.4 °C = -24 = 4294967272). PD-16191 — it must be decoded as signed before
 * the plausibility band is applied. Applying the band to the unsigned value
 * discarded every below-freezing reading; the merchandisers are freezers, so
 * that was most of the fleet's temperatures.
 */

const UINT32_RANGE = 2 ** 32;
const INT32_MAX = 2 ** 31 - 1;

// Anything outside a physically plausible band for a merchandiser zone is
// "no reading", not a temperature — letting it through poisoned the
// merchandiser temp bubble and opened false temperature alerts (PD-15445).
const TEMP_RAW_MIN = -600; // -60.0 °C
const TEMP_RAW_MAX = 700; // 70.0 °C

/**
 * Parse a raw module temperature (0.1 °C units, uint32 on the wire) into °C.
 * Wrapped negatives are decoded and already-signed values pass through;
 * non-numeric, non-finite and physically implausible values are null. Only
 * numbers are accepted, as the cellular payload sends them; a caller reading
 * text (the Bluetooth ranging block) converts it first.
 *
 * 4294967294 (-0.2 °C) is deliberately NOT treated as a no-reading
 * placeholder: in production payloads it occurs at the same rate as the
 * neighboring temperatures and between ordinary readings on the same module
 * (measured 2026-09-17), so it is a real temperature.
 */
export function parseModuleTemperatureC(raw: unknown): number | null {
	if (typeof raw !== 'number' || !Number.isFinite(raw)) {
		return null;
	}

	const signed =
		raw > INT32_MAX && raw < UINT32_RANGE ? raw - UINT32_RANGE : raw;

	if (signed < TEMP_RAW_MIN || signed > TEMP_RAW_MAX) {
		return null;
	}

	return Math.round((signed / 10) * 100) / 100;
}

// The cellular board runs warmer than any merchandiser zone (sealed in the
// head unit, next to the modem), so its band is wider at the top.
const BOARD_TEMP_MIN_C = -60;
const BOARD_TEMP_MAX_C = 100;

/**
 * PD-16194 — the head unit's board temperature (`status.tmpr` on the
 * cellular payload, `tmpr` in the Bluetooth status block), in whole °C.
 *
 * The unit is confirmed from the manufacturer's own configuration tool
 * (BX_BLE, 2026-05-27 build), read 2026-09-17: it shows `tmpr` as the raw
 * text with "℃" appended, and converts to ℉ as `toFloat(text) × 1.8 + 32` —
 * whole degrees, no scaling. (Its module temperatures, by contrast, are
 * `toFloat(text) ÷ 10`, matching `parseModuleTemperatureC`.) The tool parses
 * the text as an ordinary signed number, so a Bluetooth reading below zero
 * arrives as "-5"; the unsigned 32-bit wrap handled here is the cellular
 * payload's encoding, seen for module temperatures in production (PD-16191),
 * and accepting it costs nothing when the value is already signed.
 */
export function parseBoardTemperatureC(raw: unknown): number | null {
	if (typeof raw !== 'number' || !Number.isFinite(raw)) {
		return null;
	}

	const signed =
		raw > INT32_MAX && raw < UINT32_RANGE ? raw - UINT32_RANGE : raw;

	if (signed < BOARD_TEMP_MIN_C || signed > BOARD_TEMP_MAX_C) {
		return null;
	}

	return signed;
}
